import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/EmptyState";
import { ParticipateButton } from "@/components/ParticipateButton";
import { CalendarDays, Plus, Loader2, Sparkles, Wand2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { showError } from "@/lib/errors";

const ALLOWED_ROLES = ["athlete", "coach", "club", "federation", "contributor", "admin", "copf", "organisation"];

// V9.8 — Bloc IA préremplissage URL en tête du dialog création d'événement.
function AiUrlPrefillBlock({ onPrefill }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!url.trim()) {
      toast.error("Colle une URL avant d'extraire.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/calendar/extract-from-url", { url: url.trim() });
      const filled = Object.entries(data.prefill || {})
        .filter(([, v]) => v !== "" && v !== false && v !== null)
        .map(([k]) => k);
      onPrefill?.(data.prefill || {}, data);
      if (data.ai_used) {
        toast.success(
          `Préremplissage IA appliqué (${filled.length} champ${filled.length > 1 ? "s" : ""}, confiance ${Math.round((data.ai_confidence || 0) * 100)} %). Vérifie / complète avant de valider.`,
        );
      } else {
        toast.info("URL analysée — seules quelques méta-données ont pu être extraites. Complète manuellement.");
      }
    } catch (e) {
      showError(e, { fallback: "Échec de l'extraction" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-dashed border-[hsl(var(--aito-lagoon))]/40 bg-[hsl(var(--aito-lagoon))]/5 p-3 mb-2">
      <div className="flex items-center gap-2 mb-2">
        <Sparkles className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
        <Label className="text-xs font-semibold">Préremplir depuis une URL (IA)</Label>
      </div>
      <p className="text-[11px] text-muted-foreground mb-2">
        Colle un lien officiel (fédération, communiqué, agenda). L'IA tentera d'extraire titre, discipline,
        date, lieu, organisateur, description, niveau et lien d'inscription. Tu valides ensuite avant d'enregistrer.
      </p>
      <div className="flex gap-2">
        <Input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://federation-exemple.pf/agenda/tournoi-2026"
          data-testid="ev-prefill-url"
          disabled={busy}
          className="text-sm"
        />
        <Button
          type="button"
          onClick={submit}
          disabled={busy || !url.trim()}
          data-testid="ev-prefill-submit"
          variant="outline"
          className="border-[hsl(var(--aito-lagoon))] text-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon))] hover:text-black shrink-0"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
          <span className="ml-1.5 hidden sm:inline">Préremplir</span>
        </Button>
      </div>
    </div>
  );
}

function CreateEventDialog({ disciplines, onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "",
    // V9.7 — date et heure séparées : seule la date est obligatoire
    date_day: "",       // YYYY-MM-DD (obligatoire)
    date_time: "",      // HH:MM (facultatif)
    time_tbd: false,    // si true → "Heure à confirmer"
    end_date: "",
    discipline: "", island: "", commune: "",
    location: "", level: "", description: "", live_url: "",
    // V9.8 — champs liés au préremplissage IA depuis URL
    organizer: "",
    source_url: "",
    registration_url: "",
    // V9.11 — Type d'événement
    kind: "competition",
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));

  // V9.8 — Applique le prefill IA en gardant les champs déjà saisis par l'utilisateur
  const applyPrefill = (prefill) => {
    setForm((f) => ({
      ...f,
      title: f.title || prefill.title || "",
      discipline: f.discipline || prefill.discipline || "",
      date_day: f.date_day || prefill.date_day || "",
      date_time: f.date_time || prefill.date_time || "",
      time_tbd: prefill.time_tbd ?? f.time_tbd,
      island: f.island || prefill.island || "",
      commune: f.commune || prefill.commune || "",
      location: f.location || prefill.location || "",
      level: f.level || prefill.level || "",
      organizer: f.organizer || prefill.organizer || "",
      description: f.description || prefill.description || "",
      registration_url: f.registration_url || prefill.registration_url || "",
      source_url: f.source_url || prefill.source_url || "",
    }));
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.title || !form.date_day) {
      toast.error("Le titre et la date sont obligatoires.");
      return;
    }
    setBusy(true);
    try {
      // V9.7 — Construction de la date ISO :
      //  - Si heure renseignée : `YYYY-MM-DDTHH:MM:00`
      //  - Si "Heure à confirmer" ou heure vide : `YYYY-MM-DDT00:00:00` + flag time_tbd
      const timePart = (!form.time_tbd && form.date_time) ? `${form.date_time}:00` : "00:00:00";
      const dateIso = new Date(`${form.date_day}T${timePart}`).toISOString();
      const payload = {
        ...form,
        date: dateIso,
        time_tbd: !!form.time_tbd || !form.date_time,
        end_date: form.end_date ? new Date(form.end_date).toISOString() : null,
      };
      // Nettoyage : retire les champs vides + champs internes au form
      delete payload.date_day;
      delete payload.date_time;
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null) delete payload[k];
      });
      const { data } = await api.post("/calendar", payload);
      toast.success("Événement ajouté au calendrier.");
      setForm({
        title: "", date_day: "", date_time: "", time_tbd: false, end_date: "",
        discipline: "", island: "", commune: "", location: "", level: "",
        description: "", live_url: "",
        organizer: "", source_url: "", registration_url: "",
        kind: "competition",
      });
      setOpen(false);
      onCreated?.(data);
    } catch (e2) {
      showError(e2, { fallback: "Échec de la création" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="calendar-create-trigger" className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">
          <Plus className="h-4 w-4 mr-1.5" />Ajouter un événement
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="calendar-create-dialog">
        <DialogHeader>
          <DialogTitle>Nouvel événement</DialogTitle>
          <DialogDescription className="text-xs">
            Tournoi, stage, sélection, journée portes ouvertes… Renseigne au moins le titre et la date.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <AiUrlPrefillBlock onPrefill={applyPrefill} />
          <div>
            <Label htmlFor="ev-title" className="text-xs">Titre *</Label>
            <Input id="ev-title" data-testid="ev-title" value={form.title} onChange={set("title")} placeholder="Ex. nom de l'événement" required />
          </div>
          <div>
            <Label htmlFor="ev-kind" className="text-xs">Type d'événement</Label>
            <Select value={form.kind || "competition"} onValueChange={(v) => setForm((f) => ({ ...f, kind: v }))}>
              <SelectTrigger id="ev-kind" data-testid="ev-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="competition">Compétition</SelectItem>
                <SelectItem value="entrainement">Entraînement / stage</SelectItem>
                <SelectItem value="manifestation">Manifestation / démo</SelectItem>
                <SelectItem value="autre">Autre</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="ev-date-day" className="text-xs">Date *</Label>
              <Input id="ev-date-day" type="date" data-testid="ev-date" value={form.date_day} onChange={set("date_day")} required />
            </div>
            <div>
              <Label htmlFor="ev-date-time" className="text-xs">
                Heure <span className="text-muted-foreground">(facultative)</span>
              </Label>
              <Input
                id="ev-date-time"
                type="time"
                data-testid="ev-time"
                value={form.date_time}
                onChange={set("date_time")}
                disabled={form.time_tbd}
                placeholder="--:--"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 -mt-1">
            <input
              id="ev-time-tbd"
              type="checkbox"
              data-testid="ev-time-tbd"
              checked={form.time_tbd}
              onChange={(e) => setForm((f) => ({ ...f, time_tbd: e.target.checked, date_time: e.target.checked ? "" : f.date_time }))}
              className="h-3.5 w-3.5 accent-[hsl(var(--aito-lagoon))]"
            />
            <Label htmlFor="ev-time-tbd" className="text-xs cursor-pointer text-muted-foreground">
              Heure à confirmer
            </Label>
          </div>
          <div>
            <Label htmlFor="ev-end" className="text-xs">Date / heure de fin (optionnel)</Label>
            <Input id="ev-end" type="datetime-local" data-testid="ev-end-date" value={form.end_date} onChange={set("end_date")} />
          </div>
          <div>
            <Label className="text-xs">Discipline</Label>
            <Select value={form.discipline || ""} onValueChange={(v) => setForm((f) => ({ ...f, discipline: v }))}>
              <SelectTrigger data-testid="ev-discipline"><SelectValue placeholder="Choisir une discipline (optionnel)" /></SelectTrigger>
              <SelectContent className="max-h-60">
                {disciplines.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="ev-island" className="text-xs">Île</Label>
              <Input id="ev-island" data-testid="ev-island" value={form.island} onChange={set("island")} placeholder="Tahiti" />
            </div>
            <div>
              <Label htmlFor="ev-commune" className="text-xs">Commune</Label>
              <Input id="ev-commune" data-testid="ev-commune" value={form.commune} onChange={set("commune")} placeholder="Papeete" />
            </div>
          </div>
          <div>
            <Label htmlFor="ev-location" className="text-xs">Lieu précis</Label>
            <Input id="ev-location" data-testid="ev-location" value={form.location} onChange={set("location")} placeholder="Salle omnisports de Fautaua" />
          </div>
          <div>
            <Label htmlFor="ev-desc" className="text-xs">Description (optionnel)</Label>
            <Textarea id="ev-desc" data-testid="ev-description" rows={3} value={form.description} onChange={set("description")} placeholder="Description rapide de l'événement…" />
          </div>
          <div>
            <Label htmlFor="ev-live" className="text-xs">URL du live (optionnel)</Label>
            <Input id="ev-live" data-testid="ev-live-url" value={form.live_url} onChange={set("live_url")} placeholder="https://youtube.com/..." />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="ev-organizer" className="text-xs">Organisateur</Label>
              <Input id="ev-organizer" data-testid="ev-organizer" value={form.organizer} onChange={set("organizer")} placeholder="Fédération, club, COPF…" />
            </div>
            <div>
              <Label htmlFor="ev-level" className="text-xs">Niveau</Label>
              <Input id="ev-level" data-testid="ev-level" value={form.level} onChange={set("level")} placeholder="International, national, régional…" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="ev-source" className="text-xs">URL source (officielle)</Label>
              <Input id="ev-source" data-testid="ev-source-url" value={form.source_url} onChange={set("source_url")} placeholder="https://federation-xxx.pf/agenda/..." />
            </div>
            <div>
              <Label htmlFor="ev-reg" className="text-xs">URL inscription</Label>
              <Input id="ev-reg" data-testid="ev-registration-url" value={form.registration_url} onChange={set("registration_url")} placeholder="https://..." />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
            <Button type="submit" disabled={busy} data-testid="ev-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
              Ajouter
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function CalendarPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [discipline, setDiscipline] = useState("all");
  const [kind, setKind] = useState(searchParams.get("kind") || "all");
  const [disciplines, setDisciplines] = useState([]);
  // V9.22 — Onglet temporel (À venir par défaut / Archives)
  const [timeframe, setTimeframe] = useState(searchParams.get("timeframe") || "upcoming");

  useEffect(() => { api.get("/disciplines").then((r) => setDisciplines(r.data.items || [])); }, []);
  const load = (disc, k, tf) => {
    const params = { limit: 100 };
    if (disc !== "all") params.discipline = disc;
    if (k && k !== "all") params.kind = k;
    if (tf === "upcoming") params.upcoming_only = true;
    return api.get("/calendar", { params }).then((r) => {
      let list = r.data.items || [];
      // V9.22 — Filtre "Archives" côté client : événements dont la date est passée
      if (tf === "archives") {
        const now = Date.now();
        list = list.filter((ev) => {
          const d = new Date(ev.date_end || ev.date).getTime();
          return !isNaN(d) && d < now;
        });
        // Tri décroissant : le plus récent d'abord
        list.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      }
      setItems(list);
    });
  };
  useEffect(() => { load(discipline, kind, timeframe); }, [discipline, kind, timeframe]);

  // Sync URL ?kind= / ?timeframe=
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (kind && kind !== "all") next.set("kind", kind); else next.delete("kind");
    if (timeframe && timeframe !== "upcoming") next.set("timeframe", timeframe); else next.delete("timeframe");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, timeframe]);

  const canCreate = useMemo(() => {
    if (!user) return false;
    return (user.roles || []).some((r) => ALLOWED_ROLES.includes(r));
  }, [user]);

  const KIND_LABEL = {
    all: "Tous les types",
    competition: "Compétitions",
    entrainement: "Entraînements / stages",
    manifestation: "Manifestations",
    autre: "Autres",
  };

  const headingByKind = {
    all: { title: "Calendrier sportif", subtitle: "L'agenda sportif unifié du Fenua." },
    competition: { title: "Compétitions à venir", subtitle: "Tournois, championnats, sélections — tous les événements officiels enregistrés comme compétitions." },
    entrainement: { title: "Entraînements & stages", subtitle: "Sessions d'entraînement et stages ouverts à venir." },
    manifestation: { title: "Manifestations", subtitle: "Démonstrations, journées portes ouvertes et autres événements grand public." },
    autre: { title: "Autres événements", subtitle: "Tout le reste." },
  };
  const heading = headingByKind[kind] || headingByKind.all;

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold break-words" data-testid="calendar-heading">{heading.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{heading.subtitle}</p>
        </div>
        {canCreate && <CreateEventDialog disciplines={disciplines} onCreated={() => load(discipline, kind, timeframe)} />}
      </div>

      {/* V9.22 — Onglets À venir / Archives */}
      <div className="mt-4 inline-flex rounded-md border p-0.5 bg-muted/40" data-testid="calendar-timeframe-tabs">
        <button
          type="button"
          onClick={() => setTimeframe("upcoming")}
          className={`px-3 py-1.5 text-xs rounded ${timeframe === "upcoming" ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="calendar-tab-upcoming"
        >
          À venir & en cours
        </button>
        <button
          type="button"
          onClick={() => setTimeframe("archives")}
          className={`px-3 py-1.5 text-xs rounded ${timeframe === "archives" ? "bg-background shadow-sm font-medium" : "text-muted-foreground hover:text-foreground"}`}
          data-testid="calendar-tab-archives"
        >
          Archives
        </button>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-3 max-w-xl">
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Type</Label>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid="calendar-kind-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {Object.entries(KIND_LABEL).map(([k, v]) => (
                <SelectItem key={k} value={k} data-testid={`kind-${k}`}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Discipline</Label>
          <Select value={discipline} onValueChange={setDiscipline}>
            <SelectTrigger data-testid="calendar-discipline-select"><SelectValue placeholder="Discipline" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les disciplines</SelectItem>
              {disciplines.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-6" data-testid="sports-calendar">
        {items.length === 0 ? <EmptyState icon={CalendarDays} title={timeframe === "archives" ? "Aucun événement archivé." : "Aucun événement programmé."} description={timeframe === "archives" ? "Les événements terminés apparaîtront ici." : "Les compétitions, tournois et stages apparaîtront ici."} /> : (
          <ul className="space-y-2">
            {items.map((ev) => (
              <li key={ev.id}>
                <Card className="p-4 aito-card-hover" data-testid="calendar-event">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex flex-col items-center justify-center w-14 h-14 rounded-lg bg-[hsl(var(--aito-black))] text-white shrink-0">
                      <span className="text-xs uppercase">{new Date(ev.date).toLocaleDateString("fr-FR", { month: "short" })}</span>
                      <span className="font-display text-xl font-bold leading-none">{new Date(ev.date).getDate()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold leading-tight break-words">{ev.title}</div>
                      <div className="text-xs text-muted-foreground capitalize break-words">
                        {[
                          ev.discipline,
                          ev.commune || ev.island,
                          ev.location,
                          ev.level,
                          // V9.7 — affichage explicite si heure non-confirmée
                          ev.time_tbd
                            ? "Heure à confirmer"
                            : new Date(ev.date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }),
                        ].filter(Boolean).join(" · ")}
                      </div>
                      {ev.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2 break-words">{ev.description}</div>}
                    </div>
                    {ev.live_url && (
                      <a href={ev.live_url} target="_blank" rel="noreferrer" className="shrink-0">
                        <Badge className="bg-[hsl(var(--aito-energy))] text-black">LIVE</Badge>
                      </a>
                    )}
                  </div>
                  {/* V9.10 — Bouton "Je participe !" + compteur participants */}
                  <div className="mt-3 pt-3 border-t border-dashed">
                    <ParticipateButton eventId={ev.id} eventTitle={ev.title} />
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
